import React, { useState } from 'react';
import { 
  Paper, Typography, Box, Button, Stack, ToggleButtonGroup, 
  ToggleButton, Divider, FormControlLabel, Switch, useTheme
} from '@mui/material';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SchoolIcon from '@mui/icons-material/School';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SettingsIcon from '@mui/icons-material/Settings';

const UserControls = () => {
  const [operationMode, setOperationMode] = useState('auto');
  const [trainingMode, setTrainingMode] = useState(false);
  const theme = useTheme();
  
  const handleOperationModeChange = (event, newMode) => {
    if (newMode !== null) {
      setOperationMode(newMode);
    }
  };
  
  return (
    <Paper 
      elevation={10} 
      sx={{ 
        p: 0, 
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(30, 30, 40, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            User Controls
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ 
        p: 3, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255,255,255,0.2)',
        }
      }}>
        <Box sx={{ 
          mb: 3,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Operation Controls
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<StopCircleIcon />}
              fullWidth
              sx={{ 
                py: 1.2,
                borderRadius: '10px',
                boxShadow: '0 4px 10px rgba(244, 67, 54, 0.3)',
                background: 'linear-gradient(45deg, #F44336 30%, #FF5252 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #E53935 30%, #FF1744 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 15px rgba(244, 67, 54, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Stop
            </Button>
            <Button 
              variant="contained" 
              startIcon={<PlayCircleFilledIcon />}
              fullWidth
              sx={{ 
                py: 1.2,
                borderRadius: '10px',
                boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
                background: `linear-gradient(45deg, ${theme.palette.bmwAccent.midBlue} 30%, ${theme.palette.bmwAccent.lightBlue} 90%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.bmwAccent.darkBlue} 30%, ${theme.palette.bmwAccent.midBlue} 90%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 15px rgba(33, 150, 243, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Start
            </Button>
          </Stack>
        </Box>
        
        <Box sx={{ 
          mb: 3,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Operation Mode
          </Typography>
          <ToggleButtonGroup
            value={operationMode}
            exclusive
            onChange={handleOperationModeChange}
            aria-label="operation mode"
            fullWidth
            sx={{
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid rgba(255, 255, 255, 0.1) !important',
                borderRadius: '10px !important',
                mx: 0.5,
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  color: theme.palette.bmwAccent.lightBlue,
                  borderColor: `${theme.palette.bmwAccent.midBlue} !important`,
                  fontWeight: 600
                }
              }
            }}
          >
            <ToggleButton value="auto" aria-label="automatic mode">
              <AutorenewIcon sx={{ mr: 1, fontSize: 18 }} />
              Automatic
            </ToggleButton>
            <ToggleButton value="manual" aria-label="manual mode">
              <DirectionsRunIcon sx={{ mr: 1, fontSize: 18 }} />
              Manual
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ 
          mb: 3,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <FormControlLabel
            control={
              <Switch 
                checked={trainingMode} 
                onChange={() => setTrainingMode(!trainingMode)} 
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.bmwAccent.midBlue,
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.bmwAccent.lightBlue,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SchoolIcon fontSize="small" sx={{ mr: 1, color: trainingMode ? theme.palette.bmwAccent.lightBlue : 'inherit' }} />
                <Typography variant="body2" sx={{ fontWeight: trainingMode ? 600 : 400, color: trainingMode ? theme.palette.bmwAccent.lightBlue : 'inherit' }}>
                  Training Mode
                </Typography>
              </Box>
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mx: 1 }}>
            In training mode, operators can guide the AI system to improve its unpacking techniques for new package types.
          </Typography>
        </Box>
      
        {operationMode === 'manual' && (
          <Box sx={{ 
            mb: 3,
            p: 2,
            borderRadius: '12px',
            background: 'rgba(0, 0, 0, 0.2)'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Manual Controls
            </Typography>
            <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  py: 1.2,
                  borderRadius: '10px',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Grip
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  py: 1.2,
                  borderRadius: '10px',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Release
              </Button>
            </Stack>
            <Stack spacing={2} direction="row">
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  py: 1.2,
                  borderRadius: '10px',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Move
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ 
                  py: 1.2,
                  borderRadius: '10px',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Rotate
              </Button>
            </Stack>
          </Box>
        )}
        
        {trainingMode && (
          <Box sx={{ mt: 'auto' }}>
            <Button 
              variant="contained" 
              fullWidth
              sx={{ 
                py: 1.5,
                borderRadius: '10px',
                fontWeight: 600,
                boxShadow: '0 4px 10px rgba(156, 39, 176, 0.3)',
                background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7B1FA2 30%, #9C27B0 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 15px rgba(156, 39, 176, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Save Training Data
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default UserControls; 